// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type BaseQueryEnhancer } from '@reduxjs/toolkit/query';
import { trackPromise } from 'react-promise-tracker';
import {
  RTK_PROMISE_TRACKER_ID,
  RTK_QUERY_TRACK_OPTION,
} from '@shared/data-access/utils/constants';

/**
 * This utility function is used to track promises when using RTK Query.
 *
 * This function is used as a query enhancer to track promises on fetch requests. It
 * takes the options from the query definition and the options from the fetch
 * request and combines them.
 *
 * The `useTrackPromise` option is used to determine if the promise should be
 * tracked. If this option is not provided, the default is `true`.
 *
 * The `trackPromiseId` option is used to specify the id of the promise tracker. If
 * this option is not provided, the default is `RTK_PROMISE_TRACKER_ID`.
 *
 * @param baseQuery - The base query function that is being enhanced.
 * @param config - The configuration options for the query.
 * @returns A function that executes the enhanced query and tracks the promise.
 */
export interface TrackPromiseQueryOptions {
  /**
   * Whether the promise should be tracked.
   *
   * @default true
   */
  useTrackPromise?: boolean;
  /**
   * The id of the promise tracker.
   *
   * @default RTK_PROMISE_TRACKER_ID
   */
  trackPromiseId?: string;
}

export const trackPromiseQuery: BaseQueryEnhancer<
  { [RTK_QUERY_TRACK_OPTION]?: boolean },
  TrackPromiseQueryOptions,
  void | TrackPromiseQueryOptions
> = (baseQuery, config) => async (args, api, extraOptions) => {
  const shouldTrack =
    args?.[RTK_QUERY_TRACK_OPTION] ??
    extraOptions?.useTrackPromise ??
    config?.useTrackPromise;

  const configTrackPromise =
    extraOptions?.trackPromiseId ??
    config?.trackPromiseId ??
    RTK_PROMISE_TRACKER_ID;

  if (shouldTrack) {
    return trackPromise(
      baseQuery(args, api, extraOptions) as Promise<any>,
      configTrackPromise,
    );
  }

  return baseQuery(args, api, extraOptions);
};
